'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCheckoutPagamento } from '@/shared/hooks/useCheckoutPagamento';
import { useToast } from '@/shared/providers';
import { useAppDispatch } from '@/store/hooks';
import { MOTIVO_REGERACAO_CLIENTE } from '../constants/financeiroCliente';
import { regerarCobrancaVencida } from '../store';
import type { CustasProcesso } from '../types/custasProcessoTypes';
import type { CobrancaProcessoClienteApi, FinanceiroProcessoCliente } from '../types/financeiroProcessoClienteTypes';
import { custasAceitamCheckout, derivarCustasDoFinanceiro } from '../utils/custasProcessoUtils';
import { useLinkCanceladoAteReleitura } from './useLinkCanceladoAteReleitura';
interface UseCustasProcessoResult {
  custas: CustasProcesso | null;
  popupBloqueado: boolean;
  /**
   * Regeração em voo, do clique até a releitura do financeiro trazer a cobrança nova. O card
   * e o banner travam regerar E pagar com isto: dois cliques viravam dois ciclos, e pagar no
   * meio abria o link da cobrança que o POST cancelou.
   */
  regerando: boolean;
  /** Cobrança cuja regeração está em voo — a aba Financeiro marca "Gerando…" na linha dela. */
  regerandoId: string | null;
  /**
   * "Pagar custas" travado: além da regeração em voo, cobre a releitura que FALHA depois de o
   * POST ter cancelado a cobrança. A falha solta `regerando` (para tentar de novo), mas mantém
   * as cobranças antigas na tela, e o link delas é o da cobrança cancelada.
   */
  pagamentoSuspenso: boolean;
  /**
   * A mesma trava, por cobrança, para a aba Financeiro. É uma instância só por tela: com duas, a
   * regeração feita na aba não travava o "Pagar custas" do banner, e vice-versa.
   */
  linkSuspenso: (cobrancaId: string) => boolean;
  pagarCustas: () => void;
  confirmarPagamento: () => void;
  voltarParaCobranca: () => void;
  regerarCobranca: () => void;
  /**
   * Regeração pedida pela linha da aba Financeiro. Passa pelo MESMO caminho do card e do banner:
   * a aba tinha o seu, e a regeração feita por ela não travava o banner durante o POST nem
   * fechava o checkout aberto das custas, que seguia pintando PROCESSANDO sobre a cobrança nova.
   */
  regerarPorId: (cobrancaId: string) => void;
}
interface UseCustasProcessoOpcoes {
  processoId: string;
  /** Refaz a leitura do financeiro — é o que o polling consulta. */
  onRecarregar: () => void;
  /**
   * Erro corrente da leitura do financeiro. O polling precisa dele: sem teto de
   * ERRO, um servidor fora do ar levaria requisição a cada 5–30 s pelos 20
   * minutos inteiros do teto de relógio.
   */
  erroLeitura: string | null;
}

/**
 * Card de custas do processo, derivado da MESMA cobrança que a aba Financeiro
 * mostra.
 *
 * Três mocks morreram aqui, e todos afirmavam fato sobre dinheiro sem perguntar
 * ao servidor:
 *
 * - `CONFIRMACAO_MOCK_MS` — um `setTimeout` de 2,5 s trocava a linha para "Paga" e
 *   anunciava "o processo seguiu para a citação". Agora a confirmação é **polling
 *   do financeiro real**: o estado só muda quando o servidor disser que mudou.
 * - `DIAS_NOVA_COBRANCA_MOCK` / `adicionarDias` — devolviam a cobrança vencida a
 *   "Em aberto" com vencimento fabricado. Agora quem regera é o servidor.
 * - O toast de "Nova cobrança gerada" saía sem nenhuma chamada de rede.
 */
export function useCustasProcesso(financeiro, cobrancas, opcoes) {
  const $ = _c(64);
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [regerandoId, setRegerandoId] = useState(null);
  const regerando = regerandoId !== null;
  const releituraAposRegerar = useRef(null);
  const {
    linkSuspenso,
    suspenderLink
  } = useLinkCanceladoAteReleitura(cobrancas);
  let t0;
  if ($[0] !== toast) {
    t0 = motivo => motivo === "TEMPO" ? toast.showError("Ainda n\xE3o identificamos o pagamento", "Se voc\xEA j\xE1 pagou, o status atualiza sozinho assim que o banco confirmar.") : toast.showError("N\xE3o conseguimos verificar o pagamento", "A consulta falhou v\xE1rias vezes seguidas. Se voc\xEA j\xE1 pagou, nada foi perdido \u2014 recarregue a p\xE1gina em alguns minutos.");
    $[0] = toast;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== opcoes.erroLeitura || $[3] !== opcoes.onRecarregar || $[4] !== t0) {
    t1 = {
      consultar: opcoes.onRecarregar,
      erroLeitura: opcoes.erroLeitura,
      aoDesistir: t0
    };
    $[2] = opcoes.erroLeitura;
    $[3] = opcoes.onRecarregar;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    fase,
    popupBloqueado,
    abrirCheckout,
    verificarPagamento,
    fecharCheckout,
    limparPopupBloqueado
  } = useCheckoutPagamento(t1);
  let t2;
  if ($[6] !== cobrancas || $[7] !== financeiro) {
    t2 = derivarCustasDoFinanceiro(financeiro, cobrancas);
    $[6] = cobrancas;
    $[7] = financeiro;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const doServidor = t2;
  let t3;
  bb0: {
    if (fase === "FECHADO" || !custasAceitamCheckout(doServidor)) {
      t3 = doServidor;
      break bb0;
    }
    const t4 = fase === "AGUARDANDO" ? "PROCESSANDO" : "VERIFICANDO";
    let t5;
    if ($[9] !== doServidor || $[10] !== t4) {
      t5 = {
        ...doServidor,
        estado: t4,
        estadoAntesDoCheckout: doServidor.estado
      };
      $[9] = doServidor;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t3 = t5;
  }
  const custas = t3;
  let t4;
  if ($[12] !== cobrancas || $[13] !== linkSuspenso || $[14] !== regerando) {
    const cobrancaDasCustas = cobrancas.find(_temp);
    t4 = regerando || cobrancaDasCustas !== undefined && linkSuspenso(cobrancaDasCustas.id);
    $[12] = cobrancas;
    $[13] = linkSuspenso;
    $[14] = regerando;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const pagamentoSuspenso = t4;
  let t5;
  if ($[16] !== doServidor?.estado || $[17] !== fase || $[18] !== fecharCheckout || $[19] !== toast) {
    t5 = () => {
      if (fase === "FECHADO" || doServidor?.estado !== "PAGA") {
        return;
      }
      fecharCheckout();
      toast.showSuccess("Pagamento confirmado", "O processo seguiu para a cita\xE7\xE3o do requerido.");
    };
    $[16] = doServidor?.estado;
    $[17] = fase;
    $[18] = fecharCheckout;
    $[19] = toast;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const t6 = doServidor?.estado;
  let t7;
  if ($[21] !== fase || $[22] !== fecharCheckout || $[23] !== t6 || $[24] !== toast) {
    t7 = [t6, fase, fecharCheckout, toast];
    $[21] = fase;
    $[22] = fecharCheckout;
    $[23] = t6;
    $[24] = toast;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  useEffect(t5, t7);
  let t8;
  let t9;
  if ($[26] !== cobrancas || $[27] !== opcoes.erroLeitura) {
    t8 = () => {
      const leituraDaRegeracao = releituraAposRegerar.current;
      if (!leituraDaRegeracao) {
        return;
      }
      if (cobrancas === leituraDaRegeracao && !opcoes.erroLeitura) {
        return;
      }
      releituraAposRegerar.current = null;
      setRegerandoId(null);
    };
    t9 = [cobrancas, opcoes.erroLeitura];
    $[26] = cobrancas;
    $[27] = opcoes.erroLeitura;
    $[28] = t8;
    $[29] = t9;
  } else {
    t8 = $[28];
    t9 = $[29];
  }
  useEffect(t8, t9);
  let t10;
  if ($[30] !== abrirCheckout || $[31] !== custas?.linkPagamento || $[32] !== pagamentoSuspenso || $[33] !== toast) {
    t10 = () => {
      if (pagamentoSuspenso) {
        return;
      }
      if (abrirCheckout(custas?.linkPagamento) === "URL_INVALIDA") {
        toast.showError("Pagamento indispon\xEDvel", "O link de pagamento ainda n\xE3o foi gerado. Tente novamente em instantes.");
      }
    };
    $[30] = abrirCheckout;
    $[31] = custas?.linkPagamento;
    $[32] = pagamentoSuspenso;
    $[33] = toast;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const pagarCustas = t10;
  let t11;
  if ($[35] !== cobrancas || $[36] !== fecharCheckout || $[37] !== limparPopupBloqueado || $[38] !== opcoes || $[39] !== suspenderLink || $[40] !== toast) {
    t11 = cobrancaId => {
      fecharCheckout();
      limparPopupBloqueado();
      toast.showSuccess("Nova cobran\xE7a gerada", "O link atualizado j\xE1 est\xE1 dispon\xEDvel aqui.");
      releituraAposRegerar.current = cobrancas;
      suspenderLink(cobrancaId);
      opcoes.onRecarregar();
    };
    $[35] = cobrancas;
    $[36] = fecharCheckout;
    $[37] = limparPopupBloqueado;
    $[38] = opcoes;
    $[39] = suspenderLink;
    $[40] = toast;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const aposRegerar = t11;
  let t12;
  if ($[42] !== aposRegerar || $[43] !== cobrancas || $[44] !== dispatch || $[45] !== opcoes.processoId || $[46] !== regerando || $[47] !== toast) {
    t12 = cobrancaId_0 => {
      if (regerando) {
        return;
      }
      if (!cobrancas.some(c_0 => c_0.id === cobrancaId_0 && c_0.acoes.includes("REGERAR"))) {
        return;
      }
      setRegerandoId(cobrancaId_0);
      dispatch(regerarCobrancaVencida({
        processoId: opcoes.processoId,
        cobrancaId: cobrancaId_0,
        motivo: MOTIVO_REGERACAO_CLIENTE
      })).then(r => {
        if (regerarCobrancaVencida.rejected.match(r)) {
          setRegerandoId(null);
          toast.showError("N\xE3o conseguimos gerar a nova cobran\xE7a", r.payload ?? "Tente de novo.");
          return;
        }
        aposRegerar(cobrancaId_0);
      }).catch(() => setRegerandoId(null));
    };
    $[42] = aposRegerar;
    $[43] = cobrancas;
    $[44] = dispatch;
    $[45] = opcoes.processoId;
    $[46] = regerando;
    $[47] = toast;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  const regerarPorId = t12;
  let t13;
  if ($[49] !== cobrancas || $[50] !== regerarPorId) {
    t13 = () => {
      const cobranca = cobrancas.find(_temp2);
      if (cobranca) {
        regerarPorId(cobranca.id);
      }
    };
    $[49] = cobrancas;
    $[50] = regerarPorId;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  const regerarCobranca = t13;
  let t14;
  if ($[52] !== custas || $[53] !== fecharCheckout || $[54] !== linkSuspenso || $[55] !== pagamentoSuspenso || $[56] !== pagarCustas || $[57] !== popupBloqueado || $[58] !== regerando || $[59] !== regerandoId || $[60] !== regerarCobranca || $[61] !== regerarPorId || $[62] !== verificarPagamento) {
    t14 = {
      custas,
      popupBloqueado,
      regerando,
      regerandoId,
      pagamentoSuspenso,
      linkSuspenso,
      pagarCustas,
      confirmarPagamento: verificarPagamento,
      voltarParaCobranca: fecharCheckout,
      regerarCobranca,
      regerarPorId
    };
    $[52] = custas;
    $[53] = fecharCheckout;
    $[54] = linkSuspenso;
    $[55] = pagamentoSuspenso;
    $[56] = pagarCustas;
    $[57] = popupBloqueado;
    $[58] = regerando;
    $[59] = regerandoId;
    $[60] = regerarCobranca;
    $[61] = regerarPorId;
    $[62] = verificarPagamento;
    $[63] = t14;
  } else {
    t14 = $[63];
  }
  return t14;
}
function _temp2(c_1) {
  return c_1.finalidade === "CUSTAS_PROCESSO" && c_1.acoes.includes("REGERAR");
}
function _temp(c) {
  return c.finalidade === "CUSTAS_PROCESSO";
}